// Web Push notification client. Works only after the app is installed to the
// home screen on iOS (Safari) and in any modern browser on Android/desktop.

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ---------- Helpers ----------

export function isPushSupported() {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

export function isStandalone() {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    // iOS reports installed PWA via navigator.standalone
    window.navigator.standalone === true
  );
}

export function isIos() {
  if (typeof window === 'undefined') return false;
  return /iP(hone|ad|od)/.test(window.navigator.userAgent);
}

export function currentPermission() {
  if (typeof Notification === 'undefined') return 'unsupported';
  return Notification.permission; // 'granted' | 'denied' | 'default'
}

// urlBase64 -> Uint8Array (needed by pushManager.subscribe)
function urlB64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const b64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

// ---------- Service worker ----------

let swRegistration = null;

export async function ensureServiceWorker() {
  if (!('serviceWorker' in navigator)) return null;
  if (swRegistration) return swRegistration;
  try {
    swRegistration = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
    return swRegistration;
  } catch (err) {
    console.error('[push] SW register failed', err);
    return null;
  }
}

// ---------- Subscribe / unsubscribe ----------

async function fetchVapidKey() {
  const res = await fetch(`${API_URL}/api/push/vapid-public-key`);
  if (!res.ok) throw new Error('vapid key unavailable');
  const { publicKey } = await res.json();
  if (!publicKey) throw new Error('vapid key empty');
  return publicKey;
}

function authHeader() {
  const token = localStorage.getItem('gntl_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export async function subscribeToPush() {
  if (!isPushSupported()) throw new Error('Push not supported on this device');
  const reg = await ensureServiceWorker();
  if (!reg) throw new Error('Service worker registration failed');

  // Permission first (this is the browser prompt users see)
  const perm = await Notification.requestPermission();
  if (perm !== 'granted') throw new Error('Notification permission denied');

  const publicKey = await fetchVapidKey();
  const appKey = urlB64ToUint8Array(publicKey);

  // Reuse an existing subscription only if it was created against the
  // current VAPID key. Otherwise force a fresh one — this is how we recover
  // from server-side VAPID key rotation without asking the user to toggle.
  let sub = await reg.pushManager.getSubscription();
  if (sub) {
    const existingKey = sub.options?.applicationServerKey;
    const same = existingKey && bufEq(existingKey, appKey);
    if (!same) {
      try { await sub.unsubscribe(); } catch { /* ignore */ }
      sub = null;
    }
  }
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: appKey,
    });
  }

  const res = await fetch(`${API_URL}/api/push/subscribe`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeader() },
    body: JSON.stringify({ subscription: sub.toJSON() }),
  });
  if (!res.ok) throw new Error('Failed to save subscription');

  // Fire a welcome push the first time the user enables notifications from
  // the installed home-screen app. Backend guards against duplicates.
  if (isStandalone()) {
    fetch(`${API_URL}/api/push/welcome`, {
      method: 'POST',
      headers: authHeader(),
    }).catch(() => {});
  }
  return sub;
}

// Byte-equality for ArrayBuffer/Uint8Array — used to compare an existing
// subscription's applicationServerKey to the current one.
function bufEq(a, b) {
  const av = a instanceof ArrayBuffer ? new Uint8Array(a) : a;
  const bv = b instanceof ArrayBuffer ? new Uint8Array(b) : b;
  if (av.byteLength !== bv.byteLength) return false;
  for (let i = 0; i < av.byteLength; i++) if (av[i] !== bv[i]) return false;
  return true;
}

// Listen for the Android/Chrome "appinstalled" lifecycle event so we can also
// fire the welcome push when the user installs the PWA without going through
// the notifications card first. The backend endpoint is idempotent and will
// simply no-op if the user hasn't subscribed to push yet.
export function registerInstallListener() {
  if (typeof window === 'undefined') return;
  if (window.__gntlInstallListenerBound) return;
  window.__gntlInstallListenerBound = true;
  window.addEventListener('appinstalled', () => {
    fetch(`${API_URL}/api/push/welcome`, {
      method: 'POST',
      headers: authHeader(),
    }).catch(() => {});
  });
}

export async function unsubscribeFromPush() {
  const reg = await ensureServiceWorker();
  if (reg) {
    const sub = await reg.pushManager.getSubscription();
    if (sub) await sub.unsubscribe();
  }
  await fetch(`${API_URL}/api/push/unsubscribe`, {
    method: 'POST',
    headers: authHeader(),
  }).catch(() => {});
}

export async function sendTestPush() {
  const res = await fetch(`${API_URL}/api/push/test`, {
    method: 'POST',
    headers: authHeader(),
  });
  if (!res.ok) throw new Error('Test push failed');
  return res.json();
}

// Best-effort welcome trigger — the backend is idempotent so this is safe to
// call whenever we detect the user is running in standalone mode with an
// active subscription (e.g. from the NotificationsCard mount safety net).
export async function requestWelcomePush() {
  try {
    const res = await fetch(`${API_URL}/api/push/welcome`, {
      method: 'POST',
      headers: authHeader(),
    });
    return res.ok ? res.json() : null;
  } catch {
    return null;
  }
}

export async function pushDebug() {
  try {
    const res = await fetch(`${API_URL}/api/push/debug`, { headers: authHeader() });
    return res.ok ? res.json() : null;
  } catch {
    return null;
  }
}

// ---------- Status ----------

export async function getPushStatus() {
  if (!isPushSupported()) {
    return { supported: false, permission: 'unsupported', subscribed: false, standalone: isStandalone(), ios: isIos() };
  }
  const reg = await ensureServiceWorker();
  const sub = reg ? await reg.pushManager.getSubscription() : null;
  return {
    supported: true,
    permission: currentPermission(),
    subscribed: Boolean(sub),
    standalone: isStandalone(),
    ios: isIos(),
  };
}
